/**
 * 该组件基于LeftMenu组件封装，用于增加左侧菜单NEW标签交互能力
 * 该组件仅处理以下2个点，其余完全跟页面调用左侧菜单时配置保持一致
 * 1、拦截组件的menuList字段props（结合newMarksState状态集合，给每一个菜单增加showNew字段值，用于判定每个菜单是否展示NEW标签）
 * 2、拦截菜单的clickMenu事件，增加记录点击带NEW标签的菜单（用于取消当前菜单显示的NEW标签）（在外界传入的调用逻辑基础上，前置插入处理逻辑）
 */
import React, { useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import LeftMenu from '@home/components/LeftMenu';
import useNewMark, { calcShowNewOfMenu } from '@intelligence/hooks/useNewMark';
import { NewMarkDisplayMode, NewMarkType } from '@intelligence/enums/common';

import type { ILeftMenuPropsType } from '@home/components/LeftMenu/type';
import type { IDBIMenuVO, IDBINewMarkMenuVO } from '@intelligence/types/menu';
import type { INewMarksState } from '@intelligence/models/newMarks';

interface INewIconMenuProps extends Omit<ILeftMenuPropsType, 'menuList'> {
  menuList: IDBIMenuVO[];
}

/**
 * 递归菜单列表，结合系统NEW标签状态Map，给每一个菜单增加showNew字段值
 * @param menus 菜单列表
 * @param newMarksState 系统NEW标签状态Map
 * @returns 菜单列表
 */
const getMenusWithShowNew = (menus: IDBIMenuVO[], newMarksState: INewMarksState): IDBINewMarkMenuVO[] => {
  if (!Array.isArray(menus) || !menus.length) return [];

  return menus.map((menu) => {
    const menuId = menu?.id || '';
    const menuNewMarksState = newMarksState?.[menuId] || {};
    const subMenus = menu?.subMenus;
    const { showNew, newMarkType } = calcShowNewOfMenu(menu, newMarksState);
    return {
      ...menu,
      showNew,
      newMarkDisplayMode: menuNewMarksState?.newMarkDisplayMode ?? NewMarkDisplayMode.RecordControlled,
      newMarkType,
      subMenus: getMenusWithShowNew(subMenus, newMarksState),
    };
  });
};

const NewIconMenu: React.FC<INewIconMenuProps> = (props) => {
  const { menuList: menuListProps, clickMenu: clickMenuProps, ...resetProps } = props;

  const { newMarksState, addNewMarkRecord } = useNewMark();

  const menuList = useMemo<IDBINewMarkMenuVO[]>(() => {
    return getMenusWithShowNew(menuListProps, newMarksState);
  }, [menuListProps, newMarksState]);

  const handleClickMenu = useMemoizedFn((menu: IDBINewMarkMenuVO) => {
    addNewMarkRecord({
      newMarkDisplayMode: menu?.newMarkDisplayMode ?? NewMarkDisplayMode.RecordControlled,
      newMarkId: menu?.id,
      newMarkType: menu?.newMarkType ?? NewMarkType.New,
    });
    return clickMenuProps?.(menu);
  });

  return <LeftMenu {...resetProps} menuList={menuList} clickMenu={handleClickMenu} />;
};

export default React.memo(NewIconMenu);
